<template>
  <div
    class="text-container"
    :class="{ textOnly: src === null || src === '' }"
    v-if="windowWidth > 768"
  >
    <div class="text-section text-section-right">
      <div class="edit" v-if="authenticated()">
        <EditButton :id="id" v-on:edit-button-clicked="toggleEditing($event)" />
      </div>
      <div class="title" v-if="!canEdit">
        <h2 v-html="editableTitle">{{ editableTitle }}</h2>
      </div>
      <div class="title" v-else>
        <editor-content :editor="titleEditor" />
      </div>
      <div class="content" v-html="editableContent" v-if="!canEdit">
        <p>{{ editableContent }}</p>
      </div>
      <div class="content" v-else>
        <TextBodyEditor
          :content="editableContent"
          v-on:textchange="updateText"
        />
      </div>
    </div>
    <div
      class="image-section"
      v-if="src"
      :class="{ 'highlighted-image': highlightedImage, editing: canEdit }"
    >
      <div>
        <img v-bind:src="src" loading="lazy" alt="Section image" />
        <div class="upload-overlay" v-if="canEdit" @click="uploadImage"></div>
        <ImageUploader :id="id" />
        <small>{{ imageFooter }}</small>
      </div>
    </div>
  </div>
  <div
    class="text-container"
    v-else
    :class="{ textOnly: src === null || src === '' }"
  >
    <div
      class="image-section"
      :class="{ 'highlighted-image': highlightedImage, editing: canEdit }"
    >
      <img v-bind:src="src" loading="lazy" alt="Section image" v-if="src" />
      <div class="image-overlay"></div>
      <div class="title">
        <h2 v-html="editableTitle">{{ editableTitle }}</h2>
      </div>
    </div>
    <div class="text-section text-section-left">
      <div class="content" v-html="editableContent">
        <p>{{ editableContent }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import EditButton from "./EditButton";
import TextBodyEditor from "./TextBodyEditor";
import ImageUploader from "./ImageUploader";
import { Editor, EditorContent } from "tiptap";
import { Heading } from "tiptap-extensions";

export default {
  name: "TextContainer",
  props: {
    id: Number,
    title: String,
    content: String,
    orientation: Boolean,
    src: String,
    imageFooter: String,
    highlightedImage: Boolean,
    editable: { type: Boolean, default: true },
  },
  components: { EditButton, EditorContent, TextBodyEditor, ImageUploader },
  data() {
    return {
      editing: false,
      titleEditor: null,
      imageEditor: null,
      editableContent: "",
      editableTitle: "",
    };
  },
  mounted() {
    this.editableTitle = this.title;
    this.editableContent = this.content;
    this.titleEditor = new Editor({
      content: `
      <h2>
      ${this.editableTitle}
      </h2>
      `,
      onUpdate: ({ getHTML }) => {
        this.editableTitle = getHTML().replace(/<[^>]+>/g, "");
      },
      extensions: [new Heading({ levels: [2] })],
    });
  },
  beforeDestroy() {
    this.titleEditor.destroy();
  },
  methods: {
    authenticated: function () {
      return this.$auth;
    },
    toggleEditing: function (id) {
      if (this.editable) {
        if (this.editing) {
          let {
            editableTitle,
            orientation,
            src,
            imageFooter,
            highlightedImage,
            editableContent,
          } = this;

          let data = {
            title: editableTitle,
            text: editableContent,
            orientation,
            src,
            imageFooter,
            highlightedImage,
          };

          this.$api.put(`/maintext/${this.id}`, data).then(console.log);
        }
        this.editing = !this.editing;
      }
    },
    updateText: function (text) {
      this.editableContent = text;
    },
    uploadImage: function (text) {
      const uploader = document.querySelector(
        ".image-edit-upload.section-id-" + this.id
      );
      uploader.click();
    },
  },
  computed: {
    canEdit() {
      return this.editable && this.editing;
    },
  },
};
</script>

<style lang="less" scoped>
@min-height: 20vw;
@max-width: 30vw;

/deep/a {
  display: contents;
}

.text-container {
  display: flex;
  justify-content: left;
  background-color: var(--primary-color);
  padding: 8px 0;
  min-height: 275px;
  border-bottom: 50px solid var(--secondary-color);

  /deep/a {
    display: inline;
  }

  .image-section,
  .image-section > img {
    width: calc(@min-height - 16px);
    max-width: calc(@max-width - 16px);
    text-align: center;

    small {
      color: #fff;
      width: 100%;
      text-align: center;
      margin: 0 auto;
    }
  }

  .image-section {
    margin: auto 16px auto 16px;
    vertical-align: middle;
    display: flex;
    flex-direction: column;

    &.highlighted-image {
      width: 100%;
      min-width: 200px;
      border: none;
      margin: 8px 16px 0 0;
      height: fit-content;
      background-color: rgba(0, 0, 0, 0.2);

      // box-shadow: 0 10px 16px 0 rgba(0, 0, 0, 0.2),
      // 0 6px 20px 0 rgba(0, 0, 0, 0.19);
    }

    div.upload-overlay {
      height: 100%;
      width: 100%;
      position: absolute;
      transform: translateY(-100%);
      background: url(/static/img/cloud.svg) no-repeat;
      background-size: 50%;
      background-position: center;
      cursor: pointer;
    }

    &.editing {
      filter: brightness(30%);
    }

    img {
      width: 100%;
    }
  }

  /deep/ .text-section {
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;

    ul li p {
      display: inline;
    }

    p,
    ul {
      // text-indent: 18px;
      margin: 8px 0;
    }

    .content {
      vertical-align: middle;
      display: flex;
      flex-direction: column;
      justify-content: space-around;
    }

    .title h2 {
      text-align: center;
      color: #3e3e3e;
      font-weight: bold;
      font-size: 2rem;
      margin-bottom: 8px;
      border-bottom: 1px solid #3e3e3e;
      padding: 0;

      &::first-letter {
        text-transform: capitalize;
      }
    }

    p {
      font-size: 1rem;
      &::first-letter {
        text-transform: capitalize;
      }
    }
    ul {
      margin: 0 16px;
      display: contents;
      text-indent: 24px;
      list-style: inside disc;
    }
  }

  .text-section-left {
    flex: 1;
    margin: 8px 0;
    margin-right: 16px;
    padding: 24px 64px;
    background-color: #eeeeee;
    color: #222;
  }

  .text-section-right {
    flex: 1;
    margin: 8px 0;
    margin-left: 16px;
    padding: 24px 64px;
    background-color: #eeeeee;
    color: #222;
  }

  .text-section {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-radius: 4px;

    & > .title {
      flex-shrink: 1;
    }

    & > .content {
      flex-grow: 1;
    }
  }

  &.textOnly {
    height: fit-content;

    .text-section {
      margin: 8px auto;
      max-width: 75%;
      clip-path: none;
      min-height: 175px;
    }
  }
}

@media screen and (max-width: 768px) {
  .text-container {
    flex-direction: column;
    height: auto;
    padding: 0;

    &.textOnly {
      .image-section {
        height: 200px;

        .title > h2 {
          text-shadow: none;
        }

        .image-overlay {
          background-color: var(--secondary-color);
          filter: saturate(0.5);
        }
      }

      .text-section {
        max-width: unset;
        margin-top: unset;
        margin: 0;
      }
    }

    .image-section {
      margin: 0;
      min-width: 100%;
      height: 200px;

      img {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        min-width: 100vw;
        object-fit: cover;
      }

      .image-overlay {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        min-width: 100vw;
        background-color: rgba(0, 0, 0, 0.5);
      }

      vertical-align: middle;
      position: relative;

      .title {
        position: absolute;
        height: 100%;
        color: white;
        width: 100%;
        margin: auto;
        vertical-align: middle;
        align-content: center;
        text-align: center;

        h2 {
          position: relative;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-shadow: 2px 2px black;
        }
      }
    }

    .text-section {
      width: 100%;
      clip-path: none;
      margin: 0;
      padding: 16px;

      .title {
        position: relative;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/TextContainer.vue